import axios from 'axios';
import Api from '../../../../services/Api';
import store from '@/store/store';

const API_URL = process.env.VUE_APP_API_URL;

/**
 * Construit les en-têtes pour les requêtes JSON.
 * @returns {object} Les en-têtes avec les clés d'API et le token d'authentification.
 */
function buildJsonHeaders() {
  const headers = {
    'Content-Type': 'application/json',
    'apikey': process.env.VUE_APP_RTEK8DSR,
    'paraphrase': process.env.VUE_APP_BJ7DC3S,
  };

  if (store.state && store.state.token) {
    headers['Authorization'] = `Bearer ${store.state.token}`;
  }

  return headers;
}

const GoService = {
  /**
   * Crée un devis pour le parcours simplifié.
   * @param {object} devisData - Les données du devis.
   * @returns {Promise<object>} La réponse de l'API.
   */
  async createDevis(devisData) {
    const url = `${API_URL}go/devis/add`;
    const assureurs = Array.isArray(devisData.assureurs) ? devisData.assureurs : [];
    const body = {
      client: {
        nom: devisData.nom,
        prenom: devisData.prenom,
        telephone: devisData.telephone
      },
      ocr_data: {
        immatriculation: devisData.ocr_data.immatriculation,
        marque: devisData.ocr_data.marque,
        modele: devisData.ocr_data.modele,
        puissance_fiscale: parseInt((devisData.ocr_data.puissanceFiscaleCustom || devisData.ocr_data.puissanceFiscale), 10),
        nombre_places: parseInt(devisData.ocr_data.nombrePlaces, 10),
        type_carburant: devisData.ocr_data.carburant,
        date_premiere_mise_en_circulation: devisData.ocr_data.dateCirculation,
        valeur_venale: Number(devisData.ocr_data.valeur_venale) || 0
      },
      duree: devisData.duree,
      dateEffet: devisData.dateEffet || devisData.date_effet,
      assureurs,
      codeAgent: devisData.codeAgent || null,
      plateforme: 'Front-Dem'
    };
    if (Array.isArray(devisData.garanties) && devisData.garanties.length > 0) {
      body.garanties = devisData.garanties;
    }
    try {
      const response = await axios.post(url, body, { headers: buildJsonHeaders() });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création du devis via GoService:", error.response || error);
      throw error;
    }
  },

  /**
   * Calcule la prime pour une liste d'assureurs sans creer de devis.
   * @param {object} params { ocr_data, assureurs[], duree, garanties[]? }
   * @returns {Promise<{duree:number,resultats:Array}>}
   */
  async calculatePrime({ ocr_data, assureurs, duree, garanties }) {
    const url = `${API_URL}go/prime/calculate`;
    const body = {
      ocr_data: {
        immatriculation: ocr_data.immatriculation,
        marque: ocr_data.marque,
        modele: ocr_data.modele,
        puissance_fiscale: parseInt((ocr_data.puissanceFiscaleCustom || ocr_data.puissanceFiscale || ocr_data.puissance_fiscale), 10) || 0,
        nombre_places: parseInt(ocr_data.nombrePlaces || ocr_data.nombre_places, 10) || 0,
        type_carburant: ocr_data.carburant || ocr_data.type_carburant || '',
        date_premiere_mise_en_circulation: ocr_data.dateCirculation || ocr_data.date_premiere_mise_en_circulation,
        valeur_venale: Number(ocr_data.valeur_venale) || 0
      },
      assureurs: Array.isArray(assureurs) ? assureurs : [],
      duree
    };
    if (Array.isArray(garanties) && garanties.length > 0) body.garanties = garanties;
    try {
      const response = await axios.post(url, body, { headers: buildJsonHeaders() });
      return response.data;
    } catch (error) {
      console.error("Erreur lors du calcul de la prime via GoService:", error.response || error);
      throw error;
    }
  },

  async validateAgent(codeAgent) {
    const url = `${API_URL}tukki/agent/validate?codeAgent=${codeAgent}`;
    try {
      const response = await axios.get(url, { headers: buildJsonHeaders() });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Vérifie si un véhicule est déjà assuré par sa plaque.
   * @param {string} immatriculation - La plaque d'immatriculation.
   * @returns {Promise<object>} Le résultat de la vérification.
   */
  async checkExistingInsurance(immatriculation) {
    const url = `${API_URL}vehicule_management/verify/${immatriculation}`;
    try {
      const response = await axios.get(url, { headers: buildJsonHeaders() });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la vérification de l'assurance:", error.response || error);
      throw error;
    }
  },

  /**
   * Récupère les détails d'un contrat.
   * @param {string|object} params - Le numéro de devis ou un objet contenant le numeroDevis.
   * @returns {Promise<object>} Les détails du contrat.
   */
  async getContratDetails(params) {
    const url = `${API_URL}go/contrat/details`;
    const body = {};
    if (params) {
      body.numeroDevis = typeof params === 'object' ? params.numeroDevis : params;
    } else {
      throw new Error("Le numéro de devis est requis.");
    }

    try {
      const response = await axios.post(url, body, { headers: buildJsonHeaders() });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des détails du contrat:", error.response || error);
      throw error;
    }
  },

  async initiateWavePayment(data) {
    const url = `wave/create/session/`;
    const origin = 'https://assuraf.com';
    const payload = {
      amount: data.montant,
      currency: "XOF",
      client_reference: "ASS381" + "_" + data.numeroDevis.replace(/\//g, '_'),
      error_url: `${origin}/payment/dem/failed`,
      success_url: `${origin}/payment/dem/wave/?mdoi=${data.numeroDevis.replace(/\//g, '_')}`
    };

    try {
      const response = await Api().post(url, payload);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'initialisation du paiement Wave:", error.response || error);
      throw error;
    }
  },

  async payWithOrangeMoneyTiaTchak(data) {
    const url = `orangemoney/onestep`;
    const payload = {
      primettc: data.montant,
      telephone_client: data.telephone,
      code_otp: data.otp,
      reference: data.numeroDevis
    };
    try {
      const response = await Api().post(url, payload);
      return response.data;
    } catch (error) {
      console.error("Erreur lors du paiement Orange Money (Tia Tchak):", error.response || error);
      throw error;
    }
  }
};

export default GoService;
//69e9f702d8a0bb168b59d7d9 id association
